<template>
  <div>
    <section class="section auth-ui-section--has-margin-top auth-ui-section--is-narrow">
      <div class="container auth-ui-container--has-centered-content">
        <v-card :classes="'auth-ui-card--is-not-full-width'">
          <div slot="header-title">
            {{ $t('auth.registerTitle') }} {{ title }}
          </div>
          <form slot="body">
            <div class="field">
              <label class="label auth-ui-label">{{ $t('auth.registerPasswordLabel') }}</label>
              <div class="control has-icons-left has-icons-right auth-ui-control-has-icons-left">
                <input
                  v-model="password"
                  :class="{ 'is-danger': errors.passwordStrength }"
                  :placeholder="$t('auth.registerPasswordPlaceholder')"
                  class="input auth-ui-input"
                  type="password"
                  autocomplete="new-password"
                  @blur="validatePasswordStrength()"
                  @focus="clearErrorFromField('passwordStrength')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-lock" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{ 'fa-exclamation-triangle': errors.passwordStrength }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{ 'is-danger': errors.passwordStrength,
                          'auth-ui-error--is-hidden': !errors.passwordStrength }"
                class="help"
              >
                <span
                  v-html="$t('auth.magicLinkRegisterPasswordStrengthError', {
                    'link': `<a href='#password-requirements'>
                            ${$t('auth.magicLinkRegisterPasswordStrengthErrorLinkText')}</a>`
                  })"
                />
              </p>
            </div>

            <div class="field">
              <label class="label auth-ui-label">{{ $t('auth.registerPasswordLabel2') }}</label>
              <div class="control has-icons-left has-icons-right auth-ui-control-has-icons-left">
                <input
                  v-model="password2"
                  :class="{ 'is-danger': errors.passwordsNotMatching }"
                  :placeholder="$t('auth.registerPasswordPlaceholder2')"
                  class="input auth-ui-input"
                  type="password"
                  autocomplete="new-password"
                  @blur="validatePasswordsMatching()"
                  @focus="clearErrorFromField('passwordsNotMatching')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-lock" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{ 'fa-exclamation-triangle': errors.passwordsNotMatching }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{ 'is-danger': errors.passwordsNotMatching,
                          'auth-ui-error--is-hidden': !errors.passwordsNotMatching }"
                class="help"
              >
                {{ $t('auth.registerPasswordMatchingError') }}
              </p>
            </div>

            <v-password-requirements />

            <div class="field">
              <div class="control">
                <div class="auth-ui-blockcheckbox-checkbox auth-ui-checkbox-container">
                  <input
                    id="auth-ui-blockcheckbox"
                    v-model="terms"
                    type="checkbox"
                    class="auth-ui-blockcheckbox"
                  >
                  <label
                    class="auth-ui-blockcheckbox-label"
                    for="auth-ui-blockcheckbox"
                  >
                    <span>
                      {{ $t('auth.registerTermsLabel') }}&nbsp;
                      <a
                        :href="termsUrl"
                        target="_blank"
                        class="auth-ui-checkbox-link"
                      >{{ $t('auth.registerTermsLink') }}</a>.
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div class="field auth-ui-magic-link-description">
              {{ $t('auth.magicLinkDescription') }}
            </div>

            <div class="field is-grouped is-grouped-centered">
              <div class="control auth-ui-button-and-spinner-container">
                <button
                  :disabled="registerButtonIsDisabled"
                  class="button is-link auth-ui-button"
                  @click.prevent="register()"
                >
                  {{ $t('auth.registerButton') }}
                </button>
              </div>
            </div>

            <article
              v-if="serverResponse.visible"
              class="auth-ui-card__message-container"
            >
              <div
                :class="{'is-danger' : serverResponse.error, 'is-success': !serverResponse.error }"
                class="message"
              >
                <div class="message-body">
                  <button
                    class="delete auth-ui-delete--is-high"
                    aria-label="delete"
                    @click="closeMessage()"
                  />
                  <span v-html="serverResponse.message" />
                </div>
              </div>
            </article>

            <div class="auth-ui-card-separator" />

            <div
              v-if="!signInIsDisabled"
              class="field is-grouped is-grouped-centered auth-ui-field--has-margin-top"
            >
              <div class="control">
                <a
                  class="auth-ui-form__link"
                  @click.prevent="continueToTarget()"
                >
                  {{ $t('auth.magicLinkSkipRegistration') }}.
                </a>
              </div>
            </div>
          </form>
        </v-card>
      </div>
    </section>
  </div>
</template>

<script>
/* ============
     * Magic link Registration Page
     * ============
     *
     * Page where the user can register via magic link.
     */

import VCard from '@/components/Card.vue';
import VPasswordRequirements from '@/components/PasswordRequirements.vue';
import Request from '@/tools/Request';

export default {
  name: 'MagicLinkRegistration',

  components: {
    VCard,
    VPasswordRequirements,
  },

  data() {
    return {
      password: '',
      password2: '',
      terms: false,
      buttonPressed: false, // eliminates button spam
      errors: {
        passwordStrength: false,
        passwordsNotMatching: false,
        terms: false,
      },
      termsUrl: process.env.VUE_APP_TERMS_URL,
      serverResponse: {
        message: '',
        visible: false,
        error: false,
      },
      signInIsDisabled: process.env.VUE_APP_SIGN_IN_DISABLED === 'true',
      registerIsDisabled: false,
      title: process.env.VUE_APP_PAGE_TITLE,
    };
  },

  computed: {
    registerButtonIsDisabled() {
      const fieldIsEmpty = !(this.password.length && this.password2.length && this.terms);
      const fieldNotValid = this.errors.passwordStrength || this.errors.passwordsNotMatching
        || this.errors.terms;
      return fieldIsEmpty || fieldNotValid || this.registerIsDisabled;
    },
  },

  mounted() {
    this.setInputFieldsToSubmitFormOnEnterPress();
  },

  methods: {

    clearErrorFromField(fieldName) {
      this.errors[fieldName] = false;
    },

    validatePasswordStrength() {
      // 10 chars, one uppercase char, one lowercase char, one special char and one number
      // eslint-disable-next-line no-useless-escape,max-len
      const regEx = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\^$*.[\]{}()?"!@#%&\/\\,><':;|_~`]).{10,}/;
      this.errors.passwordStrength = !regEx.test(this.password);
    },

    validatePasswordsMatching() {
      this.errors.passwordsNotMatching = this.password !== this.password2;
    },

    doFormValidations() {
      this.validatePasswordsMatching();
      this.validatePasswordStrength();
    },

    closeMessage() {
      this.serverResponse.visible = false;
    },

    showMessage(message, isError) {
      this.serverResponse.visible = true;
      this.serverResponse.error = isError;
      this.serverResponse.message = message;
    },

    register() {
      this.doFormValidations();

      if (!this.buttonPressed && !this.registerButtonIsDisabled) {
        this.buttonPressed = true;
        this.toggleShowSpinner(true);
        this.closeMessage();

        Request.submit(
          'POST',
          `${process.env.VUE_APP_BACKEND_URL}/magic_link_registration`,
          {
            password: this.password,
            terms: this.terms,
          },
        ).then((response) => {
          this.toggleShowSpinner(false);
          if (response.registered) {
            this.registerIsDisabled = true;
            this.continueToTarget(response.target);
          } else {
            // This should never happen, as the server returns "registered" in the response,
            // unless the response contains an error which is then handled in the catch block
            this.buttonPressed = false;
            this.showMessage(this.$t('auth.registerServerErrorMessage'), true);
          }
        }).catch((error) => {
          this.toggleShowSpinner(false);
          this.buttonPressed = false;
          if (!error.error || error.error.errorCode === 'email-invalid') {
            this.showMessage(this.$t('auth.registerServerErrorMessage'), true);
          } else if (error.error.errorCode === 'email-invalid') {
            this.showMessage(this.$t('auth.registerErrorMessageEmailInvalid'), true);
          } else if (error.error.errorCode === 'username-exists') {
            this.showMessage(this.$t('auth.registerErrorMessageEmailInUse'), true);
          } else if (error.error === 'InvalidPasswordException' || error.error === 'InvalidParameterException') {
            this.showMessage(this.$t('auth.magicLinkRegisterPasswordStrengthError', {
              link: `<a href='#password-requirements'>
                            ${this.$t('auth.magicLinkRegisterPasswordStrengthErrorLinkText')}</a>`,
            }), true);
          } else {
            this.showMessage(this.$t('auth.registerServerErrorMessage'), true);
          }
        });
      }
    },

    continueToTarget(target) {
      const url = target || this.$store.state.route.query.target;
      window.location.replace(url);
    },

    setInputFieldsToSubmitFormOnEnterPress() {
      const inputs = document.getElementsByTagName('input');

      for (let i = 0; i < inputs.length; i += 1) {
        inputs[i].onkeydown = (e) => {
          if (e.keyCode === 13) {
            this.register();
          }
        };
      }
    },

    /**
     * Toggles the app-wide spinner state in Vuex store.
     */
    toggleShowSpinner(isVisible = true) {
      this.$store.dispatch('general/setShowSpinner', isVisible);
    },
  },
};
</script>
